// src/components/guest/sections/Services.jsx
import { useState, useEffect } from 'react';
import { 
  FaTools,
  FaLeaf,
  FaSeedling,
  FaSnowflake,
  FaTree,
  FaWater,
  FaHammer,
  FaCut,
  FaSpa,
  FaHome,
  FaGlobe,
  FaLightbulb,
  FaExpandArrowsAlt
} from 'react-icons/fa';
import { GiTreeBranch } from "react-icons/gi";
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useServiceList } from '../../../hooks/useServiceList';
import ServiceGalleryModal from './ServiceGalleryModal';

// Icon mapping
const iconMap = {
  FaTools,
  FaLeaf,
  FaSeedling,
  FaSnowflake,
  FaTree,
  FaWater,
  FaHammer,
  FaCut,
  FaSpa,
  FaHome,
  FaGlobe,
  FaLightbulb,
  GiTreeBranch
};

const Services = () => {
  const { services, loading } = useServiceList();
  const [serviceImages, setServiceImages] = useState({});
  const [imagesLoading, setImagesLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);

  // Load images for all services
  useEffect(() => {
    const loadServiceImages = async () => {
      if (!services || services.length === 0) {
        setImagesLoading(false);
        return;
      }

      setImagesLoading(true);
      
      try {
        const imagesRef = collection(db, 'gallery_images');
        const imagesByService = {};
        
        // Load images for each service
        for (const service of services) {
          const q = query(
            imagesRef,
            where('category', '==', service.name),
            where('isActive', '==', true)
          );
          
          const querySnapshot = await getDocs(q);
          const images = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Sort: displayFirst images go first, then by upload date
          images.sort((a, b) => {
            if (a.displayFirst && !b.displayFirst) return -1;
            if (!a.displayFirst && b.displayFirst) return 1;
            return (a.uploadedAt?.toDate()?.getTime() ?? 0) - (b.uploadedAt?.toDate()?.getTime() ?? 0);
          });

          imagesByService[service.name] = images;
        }
        
        setServiceImages(imagesByService);
      } catch (err) {
        console.error('Error loading service images:', err);
      } finally {
        setImagesLoading(false);
      }
    };
    
    loadServiceImages();
  }, [services]);

  const getIconComponent = (iconName) => {
    return iconMap[iconName] || FaTools;
  };

  const getServicePreviewImages = (service) => {
    const images = serviceImages[service.name] || [];
    const maxPreview = 1;
    return images.slice(0, maxPreview);
  };

  const getServiceImageCount = (service) => {
    return (serviceImages[service.name] || []).length;
  };

  const handleViewGallery = (service) => {
    setSelectedService(service);
    setShowGalleryModal(true);
  };

  if (loading || imagesLoading) {
    return (
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From maintenance to installation, we provide comprehensive landscaping services 
              to meet all your outdoor needs throughout every season.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 animate-pulse">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gray-300 rounded"></div>
                  <div className="w-32 h-6 bg-gray-300 rounded ml-3"></div>
                </div>
                <div className="space-y-2 mb-6">
                  <div className="w-full h-4 bg-gray-300 rounded"></div>
                  <div className="w-3/4 h-4 bg-gray-300 rounded"></div>
                </div>
                <div className="h-64 bg-gray-300 rounded-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From design to maintenance, we provide comprehensive landscaping services 
            to meet all your outdoor needs throughout every season.
          </p>
        </div>

        {services && services.length > 0 ? (
          <>
            {/* Services with images - Main grid */}
            {(() => {
              // Sort services by displayOrder first
              const sortedServices = [...services].sort((a, b) => 
                (a.displayOrder || 999) - (b.displayOrder || 999)
              );

              const servicesWithImages = sortedServices.filter(service => {
                const imageCount = getServiceImageCount(service);
                return imageCount > 0;
              });
              
              const servicesWithoutImages = sortedServices.filter(service => {
                const imageCount = getServiceImageCount(service);
                return imageCount === 0;
              });

              return (
                <>
                  {servicesWithImages.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                      {servicesWithImages.map((service, index) => {
                        const IconComponent = getIconComponent(service.icon);
                        const previewImages = getServicePreviewImages(service);
                        const totalImages = getServiceImageCount(service);
                        
                        return (
                          <div 
                            key={service.name || index}
                            className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 group"
                          >
                            <div className="p-8">
                              <div className="flex items-center mb-4">
                                <div className="text-primary-600 group-hover:text-primary-700 transition-colors">
                                  <IconComponent className="w-8 h-8" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 ml-3">
                                  {service.name}
                                </h3>
                              </div>
                              
                              {service.description && (
                                <p className="text-gray-600 mb-6 leading-relaxed">
                                  {service.description}
                                </p>
                              )}
                            </div>
                            
                            {previewImages.length > 0 && (
                              <div className="px-8 pb-8">
                                {/* Single large preview image */}
                                <div className="relative h-64 rounded-lg overflow-hidden group/image mb-4">
                                  <img
                                    src={previewImages[0].url}
                                    alt={previewImages[0].alt || `${service.name} example`}
                                    className="w-full h-full object-cover transition-transform duration-300 group-hover/image:scale-110"
                                    loading="lazy"
                                  />
                                  <div className="absolute inset-0 bg-black opacity-0 group-hover/image:opacity-20 transition-opacity duration-300" />
                                </div>
                                
                                {totalImages > 1 && (
                                  <button
                                    onClick={() => handleViewGallery(service)}
                                    className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                                  >
                                    <FaExpandArrowsAlt className="w-4 h-4" />
                                    <span>View All {totalImages} Photos</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Services without images - Compact section */}
                  {servicesWithoutImages.length > 0 && (
                    <div className="mt-12">
                      <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                        Additional Services
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {servicesWithoutImages.map((service, index) => {
                          const IconComponent = getIconComponent(service.icon);
                          
                          return (
                            <div 
                              key={service.name || index}
                              className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300"
                            >
                              <div className="flex items-center mb-3">
                                <div className="text-primary-600">
                                  <IconComponent className="w-6 h-6" />
                                </div>
                                <h4 className="text-lg font-semibold text-gray-900 ml-3">
                                  {service.name}
                                </h4>
                              </div>
                              
                              {service.description && (
                                <p className="text-sm text-gray-600 leading-relaxed">
                                  {service.description}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">No services available at the moment.</p>
          </div>
        )}
      </div>

      {/* Gallery Modal */}
      {selectedService && (
        <ServiceGalleryModal
          service={selectedService}
          images={serviceImages[selectedService.name] || []}
          isOpen={showGalleryModal}
          onClose={() => {
            setShowGalleryModal(false);
            setSelectedService(null);
          }}
        />
      )}
    </section>
  );
};

export default Services;